"use client"

import { type FormEvent, type ReactNode, useRef, useState } from "react"

import { Button } from "@repo/ui/components/button"
import { Input } from "@repo/ui/components/input"
import { Label } from "@repo/ui/components/label"

/** Resolves to an error message to show, or `null` when the call succeeded. */
type Outcome = Promise<string | null>

function FormHeading({
  title,
  description,
}: {
  title: string
  description: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2 text-center">
      <h1 className="font-sans text-2xl font-semibold tracking-tight">
        {title}
      </h1>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  )
}

function FormError({ message }: { message: string | null }) {
  if (!message) {
    return null
  }

  return (
    <p className="text-sm text-destructive" role="alert">
      {message}
    </p>
  )
}

function Notice({
  title,
  children,
  action,
}: {
  title: string
  children: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex w-full flex-col items-center gap-4 text-center">
      <FormHeading title={title} description={children} />
      {action}
    </div>
  )
}

type LoginStatus = "idle" | "signing-in" | "sending-link" | "link-sent"

function LoginForm({
  onPasswordSignIn,
  onMagicLink,
  forgotPasswordLink,
}: {
  onPasswordSignIn: (email: string, password: string) => Outcome
  onMagicLink: (email: string) => Outcome
  /** Rendered next to the password label, e.g. a router `<Link>`. */
  forgotPasswordLink?: ReactNode
}) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [status, setStatus] = useState<LoginStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  const emailRef = useRef<HTMLInputElement>(null)

  const busy = status === "signing-in" || status === "sending-link"

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setStatus("signing-in")
    setError(null)

    const message = await onPasswordSignIn(email, password)

    if (message) {
      setError(message)
    }

    setStatus("idle")
  }

  async function handleMagicLink() {
    if (!emailRef.current?.reportValidity()) {
      return
    }

    setStatus("sending-link")
    setError(null)

    const message = await onMagicLink(email)

    if (message) {
      setError(message)
      setStatus("idle")
      return
    }

    setStatus("link-sent")
  }

  if (status === "link-sent") {
    return (
      <Notice
        title="Check your email"
        action={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setStatus("idle")}
          >
            Back to sign in
          </Button>
        }
      >
        We sent a sign-in link to{" "}
        <span className="font-medium text-foreground">{email}</span>.
      </Notice>
    )
  }

  return (
    <div className="flex w-full flex-col gap-8">
      <FormHeading
        title="Sign in"
        description="Enter your email and password to continue."
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            ref={emailRef}
            id="email"
            type="email"
            autoComplete="username"
            placeholder="you@example.com"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            {forgotPasswordLink && (
              <span className="text-xs text-muted-foreground underline-offset-4 hover:underline">
                {forgotPasswordLink}
              </span>
            )}
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <FormError message={error} />

        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {status === "signing-in" ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>

      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full"
        disabled={busy}
        onClick={handleMagicLink}
      >
        {status === "sending-link"
          ? "Sending link…"
          : "Email me a sign-in link"}
      </Button>
    </div>
  )
}

function ForgotPasswordForm({
  onSubmit,
  backLink,
}: {
  onSubmit: (email: string) => Outcome
  backLink: ReactNode
}) {
  const [email, setEmail] = useState("")
  const [sent, setSent] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setPending(true)
    setError(null)

    const message = await onSubmit(email)

    setPending(false)

    if (message) {
      setError(message)
      return
    }

    setSent(true)
  }

  if (sent) {
    return (
      <Notice title="Check your email" action={backLink}>
        If an account exists for{" "}
        <span className="font-medium text-foreground">{email}</span>,
        we&rsquo;ve sent a link to choose a new password.
      </Notice>
    )
  }

  return (
    <div className="flex w-full flex-col gap-8">
      <FormHeading
        title="Forgot password"
        description="Enter your email and we’ll send you a link to set a new password."
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            placeholder="you@example.com"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <FormError message={error} />

        <Button type="submit" size="lg" className="w-full" disabled={pending}>
          {pending ? "Sending link…" : "Send reset link"}
        </Button>
      </form>

      <div className="text-center text-sm">{backLink}</div>
    </div>
  )
}

function ResetPasswordForm({
  onSubmit,
  minLength,
  signInLink,
}: {
  onSubmit: (newPassword: string) => Outcome
  minLength: number
  signInLink: ReactNode
}) {
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [done, setDone] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (password !== confirm) {
      setError("Passwords don’t match.")
      return
    }

    setPending(true)
    setError(null)

    const message = await onSubmit(password)

    setPending(false)

    if (message) {
      setError(message)
      return
    }

    setDone(true)
  }

  if (done) {
    return (
      <Notice title="Password updated" action={signInLink}>
        You can now sign in with your new password.
      </Notice>
    )
  }

  return (
    <div className="flex w-full flex-col gap-8">
      <FormHeading
        title="Set a new password"
        description={`Use at least ${minLength} characters.`}
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <Label htmlFor="new-password">New password</Label>
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={minLength}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="confirm-password">Confirm password</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={minLength}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </div>

        <FormError message={error} />

        <Button type="submit" size="lg" className="w-full" disabled={pending}>
          {pending ? "Saving…" : "Update password"}
        </Button>
      </form>
    </div>
  )
}

export { ForgotPasswordForm, LoginForm, ResetPasswordForm }
export type { Outcome as AuthOutcome }
